import { WIDE_DESKTOP_MIN_WIDTH } from 'constants/breakpoints'
import { sortBy } from 'lodash-es'
import { useMediaQuery } from '@mui/material'

import { RiskThresholdLevelName } from 'app/services/api/acceptanceCategories'
import { selectDataLabel } from 'common/utils/chart/chartUtils'
import { QRABarColorScheme } from 'common/utils/colorUtils'

import { ILocalFilter } from 'features/RiskManagement/Components/LocalFilter'
import { GenericDonutChart } from 'features/RiskManagement/QRA/GraphsView/GenericDonutChart'

import {
  IChartDataValues,
  IQRAChartObjects,
  IQRARiskForecastChartObject,
  QRAChartItems,
  QRAGraphType,
  QRARiskForecastChartItems
} from '.'
import GraphContainerCard from './Components/GraphContainerCard'
import {
  filterS2Values,
  getLRGraphData,
  getStandardChartCVPairs
} from './graphUtils'
import ConsequencesChart from './Implementations/ConsequencesChart'
import LinearReferenceChart from './Implementations/GraphLinearReference'
import NoDataChart from './Implementations/NoDataChart'
import RiskByLineChart from './Implementations/RiskByLineChart'
import RiskForecastChart, {
  IRiskForecastChartYearSeriesItem
} from './Implementations/RiskForecastChart'
import ThreatsChart from './Implementations/ThreatsChart'

type GetGraphComponentByIdProps = {
  graphType: string
  showLoading: boolean
  charts: IQRAChartObjects
  localFilter: ILocalFilter
  qraBasePath?: string
  itemsFromGraphType?: Record<string, () => QRAChartItems[] | undefined>
}

export const GraphComponentById = ({
  graphType,
  showLoading,
  charts,
  localFilter,
  qraBasePath,
  itemsFromGraphType
}: GetGraphComponentByIdProps) => {
  const isWideDesktop = useMediaQuery(`(min-width:${WIDE_DESKTOP_MIN_WIDTH}px)`)

  switch (graphType) {
    case QRAGraphType.THREATS: {
      const { categories, values, labels } = getStandardChartCVPairs(
        charts,
        graphType
      )

      const stateNames = Object.keys(values)

      if (!showLoading && stateNames.length < 1) {
        return <NoDataChart graphType={graphType} />
      }

      return (
        <ThreatsChart
          showLoading={showLoading}
          categories={categories}
          s1Values={values[stateNames[0]]}
          s1Label={labels[stateNames[0]]}
          s2Values={values[stateNames[1]]}
          s2Label={labels[stateNames[1]]}
          colorScheme={QRABarColorScheme.THREATS}
        />
      )
    }

    case QRAGraphType.CONSEQUENCES: {
      const { categories, values, labels } = getStandardChartCVPairs(
        charts,
        graphType
      )

      const stateNames = Object.keys(values)

      if (!showLoading && stateNames.length < 1) {
        return <NoDataChart graphType={graphType} />
      }

      return (
        <ConsequencesChart
          showLoading={showLoading}
          categories={categories}
          s1Values={values[stateNames[0]]}
          s1Label={labels[stateNames[0]]}
          s2Values={values[stateNames[1]]}
          s2Label={labels[stateNames[1]]}
          colorScheme={QRABarColorScheme.CONSEQUENCES}
        />
      )
    }

    case QRAGraphType.RISK_BY_LINE: {
      const { categoryIds, categories, values, labels } =
        getStandardChartCVPairs(charts, graphType)

      const stateNames = Object.keys(values)

      if (!showLoading && stateNames.length < 1) {
        return <NoDataChart graphType={graphType} />
      }

      return (
        <RiskByLineChart
          showLoading={showLoading}
          categoryIds={categoryIds}
          categories={categories}
          catInteractNavBasePath={
            qraBasePath == null ? undefined : qraBasePath + '/line'
          }
          s1Values={values[stateNames[0]]}
          s1Label={labels[stateNames[0]]}
          s2Values={values[stateNames[1]]}
          s2Label={labels[stateNames[1]]}
          colorScheme={QRABarColorScheme.RISK_BY_LINE}
          isWideDesktop={isWideDesktop}
        />
      )
    }

    case QRAGraphType.LINEAR_REFERENCE: {
      const { XValues, seriesValues, threshold, seriesLabels, measureValues } =
        getLRGraphData(charts, graphType)

      const stateNames = Object.keys(seriesValues)

      if (!showLoading && stateNames.length < 1) {
        return <NoDataChart graphType={graphType} />
      }

      const getLocalFilterRoutes = (currentLine: string) =>
        sortBy(localFilter.segments.filter((segs) => segs.name === currentLine))

      return (
        <LinearReferenceChart
          showLoading={showLoading}
          XValues={XValues}
          s1Values={seriesValues[stateNames[0]] ?? []}
          s2Values={seriesValues[stateNames[1]] ?? []}
          s1Label={seriesLabels[stateNames[0]]}
          s2Label={seriesLabels[stateNames[1]]}
          YBarValues1={seriesValues[stateNames[0]]}
          YBarValues2={stateNames[1] == null ? [] : seriesValues[stateNames[1]]}
          // To-Do: threshold functionality needs to be updated
          threshold={threshold}
          measureValues={measureValues}
          getLocalFilterRoutes={getLocalFilterRoutes}
        />
      )
    }

    case QRAGraphType.RISK_FORECAST: {
      /**
       * NOTE: Special data structure and logic, no point in creating
       *       abstractions that is not reused anywhere else.
       */

      const chartObject = charts[graphType] as
        | IQRARiskForecastChartObject
        | undefined

      const years: number[] = []
      const baseSeriesPerYear: IRiskForecastChartYearSeriesItem[][] = []
      const compareSeriesPerYear: IRiskForecastChartYearSeriesItem[][] = []
      const stateNames: string[] = []
      const labels: string[] = []

      let riskForecastItems = chartObject?.items

      if (itemsFromGraphType) {
        riskForecastItems = itemsFromGraphType[
          QRAGraphType.RISK_FORECAST
        ]() as QRARiskForecastChartItems[]
      }

      if (riskForecastItems) {
        for (const i of riskForecastItems) {
          for (const cur of i.items) {
            for (const categoryValue of cur.values) {
              if (!stateNames.includes(categoryValue.key)) {
                stateNames.push(categoryValue.key)
                labels.push(selectDataLabel(categoryValue))
              }
            }
          }
        }

        for (const { year, items } of riskForecastItems) {
          years.push(year)
          baseSeriesPerYear.push([])
          compareSeriesPerYear.push([])

          const yearIdx = years.length - 1

          for (const { name, values } of items) {
            const baselineValue = values.find(
              (v) => v.key.toLowerCase() === stateNames[0]?.toLocaleLowerCase()
            )

            const compareValue = values.find(
              (v) => v.key.toLowerCase() !== stateNames[0]?.toLocaleLowerCase()
            )

            if (baselineValue != null) {
              baseSeriesPerYear[yearIdx].push({
                riskType: name,
                value: baselineValue.value
              })
            }

            if (compareValue != null) {
              compareSeriesPerYear[yearIdx].push({
                riskType: name,
                value: compareValue.value
              })
            }
          }
        }
      }

      if (!showLoading && years.length < 1) {
        return <NoDataChart graphType={graphType} />
      }

      return (
        <RiskForecastChart
          showLoading={showLoading}
          years={years}
          baseSeriesPerYear={baseSeriesPerYear}
          compareSeriesPerYear={compareSeriesPerYear}
          s1Label={labels[0]}
          s2Label={labels[1]}
          colorScheme={QRABarColorScheme.RISK_BY_LINE}
          isWideDesktop={isWideDesktop}
        />
      )
    }

    case QRAGraphType.RISK_BY_SIGNIFICANCE_LEVEL: {
      const { categories, values } = getStandardChartCVPairs(charts, graphType)
      const stateNames = Object.keys(values)

      if (!showLoading && stateNames.length < 1) {
        return <NoDataChart graphType={graphType} />
      }

      // Custom order for categories, but only include those present
      const customCategoryOrder = [
        RiskThresholdLevelName.LowRisk,
        RiskThresholdLevelName.MediumLowRisk,
        RiskThresholdLevelName.MediumRisk,
        RiskThresholdLevelName.MediumHighRisk,
        RiskThresholdLevelName.HighRisk
      ]

      // Filter and sort categories and values by custom order
      const sortedCategories = customCategoryOrder.filter((cat) =>
        categories.includes(cat)
      )
      const sortedIndices = sortedCategories.map((cat) =>
        categories.indexOf(cat)
      )

      const s1Values = values[stateNames[0]]
      const s2Values = values[stateNames[1]]

      const sortedS1Values = s1Values
        ? sortedIndices.map((i) => s1Values[i])
        : s1Values
      const sortedS2Values = s2Values
        ? sortedIndices.map((i) => s2Values[i])
        : s2Values

      return (
        <GenericDonutChart
          showLoading={showLoading}
          title='Length of Pipe by Risk Significance Level'
          graphType={graphType}
          categories={sortedCategories}
          s1Values={sortedS1Values}
          s2Values={sortedS2Values}
          s1Label={stateNames[0]}
          s2Label={stateNames[1]}
        />
      )
    }

    case QRAGraphType.RISK_BY_SYSTEM: {
      const { categories, values } = getStandardChartCVPairs(charts, graphType)
      const stateNames = Object.keys(values)

      const nonAllZerosValues: IChartDataValues = {}
      const nonAllZerosValuesStateNames: string[] = []

      for (const state of stateNames) {
        const stateHasNonZeroValue = values[state].some((value) => value !== 0)

        if (stateHasNonZeroValue) {
          nonAllZerosValues[state] = values[state]
          nonAllZerosValuesStateNames.push(state)
        }
      }

      if (!showLoading && stateNames.length < 1) {
        return <NoDataChart graphType={graphType} />
      }

      const s1Values = nonAllZerosValues[nonAllZerosValuesStateNames[0]]
      let s2Values = nonAllZerosValues[nonAllZerosValuesStateNames[1]]

      //BE isn't retrieving s2Values as zero when they are local filtered out opposed to what BE with s1Values does.
      //therefore I base myself on s1 values to delete s2 values
      if (s2Values && s2Values.length > 0) {
        s2Values = filterS2Values(
          s1Values,
          s2Values,
          localFilter.riskLevels,
          categories
        )
      }

      return (
        <GenericDonutChart
          showLoading={showLoading}
          title='Risk by System'
          categories={categories}
          s1Values={s1Values}
          s2Values={s2Values}
          s1Label={stateNames[0]}
          s2Label={stateNames[1]}
        />
      )
    }

    default:
      return (
        <GraphContainerCard
          title={graphType}
          optionsComponent={<div>component</div>}
        >
          Under Construction
        </GraphContainerCard>
      )
  }
}
// #endregion
